import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import type { InputType } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { downloadS3Documents, getFloodOrder, saveFloodOrderInfo, submitFloodOrder, syncNationalFlood } from 'services'
import { svgLoading } from 'stories/assets'
import { Button, ButtonGroup } from 'stories/components'
import { formatDateYMD, InputConvert, InputValidate, validateGoogleAddress } from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import {
  borrowerPropertyInputGroups,
  defaultInputs,
  floodProductTypes,
  floodResultInfoInputGroups,
  ServiceLinkOptions,
} from './constants'
import { ServiceLinkOrderModal } from './ServiceLinkOrderModal'
import type { IFloodProperty, INationalFloodOrder } from './types'

const SUBJECT_PROPERTY_KEY = 'subject'

const applyPropertyInfoToInputs = (
  baseInputs: Record<string, InputType>,
  propertyInfo: Record<string, any>,
): Record<string, InputType> => {
  const newInputs = cloneDeep(baseInputs)
  Object.keys(propertyInfo || {}).forEach((key: string) => {
    if (!newInputs[key]) return
    const value = propertyInfo[key]
    newInputs[key].value = value ?? newInputs[key].value
    if (newInputs[key].type === 'date' && typeof value === 'string' && value.length > 0) {
      newInputs[key].value = formatDateYMD(value)
    }
  })
  return newInputs
}

export default function NationalFlood({ child = false, title }: { child?: boolean; title?: string }) {
  const screenTitle = title?.trim() || 'Flood'
  const [loading, setLoading] = useState(false)
  const [borrowerLoading, setBorrowerLoading] = useState(false)
  const [floodLoading, setFloodLoading] = useState(false)
  const [orders, setOrders] = useState<INationalFloodOrder[]>([])
  const [infoByProperty, setInfoByProperty] = useState<Record<string, Record<string, any>>>({})
  const [properties, setProperties] = useState<IFloodProperty[]>([])
  const [selectedPropertyKey, setSelectedPropertyKey] = useState(SUBJECT_PROPERTY_KEY)
  const [inputs, setInputs] = useState(defaultInputs())
  const [isModalOpened, setModalOpened] = useState(false)
  const [fieldValueChanged, setFieldValueChanged] = useState(false)
  const [action, setAction] = useState('')
  const [includeCoborrower, setIncludeCoborrower] = useState(false)

  useEffect(() => {
    setLoanNumber()
    setDefaultValues()
  }, [])

  const propertyTabs = useMemo(() => {
    const tabs: Record<string, string> = {}
    properties.forEach((prop) => {
      tabs[prop.key] = prop.address
    })
    return tabs
  }, [properties])

  const visibleOrders = useMemo(() => {
    return orders.filter((order) => (order.propertyKey || SUBJECT_PROPERTY_KEY) === selectedPropertyKey)
  }, [orders, selectedPropertyKey])

  const certificateNumbersByVendor = useMemo(() => {
    const byVendor: Record<string, string[]> = {}
    visibleOrders.forEach(({ vendor, certificate_number }) => {
      if (!certificate_number || !vendor) return
      if (!byVendor[vendor]) byVendor[vendor] = []
      if (!byVendor[vendor].includes(certificate_number)) byVendor[vendor].push(certificate_number)
    })
    // Historical DV flood orders used VendorKey.Dataverify; new ones use DataverifyFlood.
    // Merge so cert pickers see both when ordering under DataverifyFlood.
    const legacy = byVendor.Dataverify || []
    const next = byVendor.DataverifyFlood || []
    const merged = [...next]
    legacy.forEach((n) => {
      if (!merged.includes(n)) merged.push(n)
    })
    if (merged.length) byVendor.DataverifyFlood = merged
    return byVendor
  }, [visibleOrders])

  const applyLoanBorrowerDefaults = (
    target: Record<string, InputType>,
    loanDetail: Record<string, any>,
  ): Record<string, InputType> => {
    const newInputs = cloneDeep(target)
    newInputs.firstName.value = loanDetail.firstName
    newInputs.middleName.value = loanDetail.middleName
    newInputs.lastName.value = loanDetail.lastName

    if (loanDetail.includeCoborrower) {
      if (!newInputs.coFirstName) {
        const defaults = defaultInputs()
        newInputs.coFirstName = defaults.coFirstName
        newInputs.coMiddleName = defaults.coMiddleName
        newInputs.coLastName = defaults.coLastName
      }
      newInputs.coFirstName.value = loanDetail.coFirstName
      newInputs.coMiddleName.value = loanDetail.coMiddleName
      newInputs.coLastName.value = loanDetail.coLastName
    } else {
      delete newInputs.coFirstName
      delete newInputs.coMiddleName
      delete newInputs.coLastName
    }
    return newInputs
  }

  const loadPropertyIntoInputs = async (
    propertyKey: string,
    infoMap: Record<string, Record<string, any>>,
    props: IFloodProperty[],
    loanDetail: Record<string, any>,
    baseInputs?: Record<string, InputType>,
  ) => {
    let newInputs = applyLoanBorrowerDefaults(baseInputs || defaultInputs(), loanDetail)
    const propertyInfo = infoMap[propertyKey] || {}
    const prop = props.find((p) => p.key === propertyKey)
    const hasStreet = !!(propertyInfo.street || propertyInfo.city)

    if (!hasStreet && prop?.address) {
      try {
        const addresses = await validateGoogleAddress(prop.address)
        newInputs.street.value = addresses.street_address1
        newInputs.city.value = addresses.city
        newInputs.state.value = addresses.state
        newInputs.zip.value = addresses.postal_code
        newInputs.county.value = prop.county || ''
      } catch {
        // Keep empty address fields if Google parse fails
      }
    }

    newInputs = applyPropertyInfoToInputs(newInputs, propertyInfo)
    if (!propertyInfo.county && prop?.county) newInputs.county.value = prop.county
    setInputs(newInputs)
  }

  const setDefaultValues = async () => {
    setLoading(true)
    const {
      data: { info = {}, orders = [] },
      loanDetail,
    } = await getFloodOrder()

    setLoading(false)

    const props: IFloodProperty[] =
      Array.isArray(loanDetail.properties) && loanDetail.properties.length
        ? loanDetail.properties
        : [
            {
              key: SUBJECT_PROPERTY_KEY,
              address: loanDetail.subjectPropertyAddress || '',
              county: loanDetail.propertyCounty,
            },
          ]

    const infoMap: Record<string, Record<string, any>> =
      info && typeof info === 'object' && (info.subject || info[SUBJECT_PROPERTY_KEY])
        ? info
        : { [SUBJECT_PROPERTY_KEY]: info || {} }

    const initialKey = props[0]?.key || SUBJECT_PROPERTY_KEY

    setProperties(props)
    setInfoByProperty(infoMap)
    setOrders(orders)
    setIncludeCoborrower(!!loanDetail.includeCoborrower)
    setSelectedPropertyKey(initialKey)
    await loadPropertyIntoInputs(initialKey, infoMap, props, loanDetail)
  }

  const onChangePropertyTab = async (key: string) => {
    if (key === selectedPropertyKey) return
    setSelectedPropertyKey(key)
    setFieldValueChanged(false)
    await loadPropertyIntoInputs(key, infoByProperty, properties, {
      firstName: inputs.firstName?.value,
      middleName: inputs.middleName?.value,
      lastName: inputs.lastName?.value,
      includeCoborrower,
      coFirstName: inputs.coFirstName?.value,
      coMiddleName: inputs.coMiddleName?.value,
      coLastName: inputs.coLastName?.value,
    })
  }

  const onChangeInput = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(newInputs)
    setInfoByProperty((prev) => ({
      ...prev,
      [selectedPropertyKey]: {
        ...(prev[selectedPropertyKey] || {}),
        [key]: value,
      },
    }))
    setFieldValueChanged(true)
  }

  const onBlurInput = async (key: string, index: number) => {
    if (!fieldValueChanged) return
    const { value } = inputs[key]

    if (index == 0) setBorrowerLoading(true)
    else setFloodLoading(true)

    await saveFloodOrderInfo({ key, value, propertyKey: selectedPropertyKey })

    if (index == 0) setBorrowerLoading(false)
    else setFloodLoading(false)
    setFieldValueChanged(false)
  }

  const onSubmitOrder = () => {
    let hasError = false
    let newInputs = cloneDeep(inputs)
    for (const key in borrowerPropertyInputGroups) {
      const keys = (borrowerPropertyInputGroups as any)[key]
      for (const inputKey of keys) {
        if (!newInputs[inputKey]) continue
        newInputs[inputKey].error = InputValidate(newInputs[inputKey])
        if (newInputs[inputKey].error) hasError = true
      }
    }
    setInputs(newInputs)
    if (hasError) return

    setModalOpened(true)
  }

  const onCloseModal = () => {
    setModalOpened(false)
  }

  const onSubmitModal = async (orderData: Record<string, any>) => {
    const {
      data: { orders: nextOrders, info },
    } = await submitFloodOrder({ ...orderData, propertyKey: selectedPropertyKey })

    setOrders(nextOrders)

    const infoMap: Record<string, Record<string, any>> =
      info && typeof info === 'object' && (info.subject || info[SUBJECT_PROPERTY_KEY] || info[selectedPropertyKey])
        ? info
        : { ...infoByProperty, [selectedPropertyKey]: { ...(infoByProperty[selectedPropertyKey] || {}), ...info } }

    setInfoByProperty(infoMap)
    const propertyInfo = infoMap[selectedPropertyKey] || {}
    setInputs(applyPropertyInfoToInputs(inputs, propertyInfo))
  }

  const onDownloadDocument = async (pdfKey: string) => {
    setLoading(true)
    const res = await downloadS3Documents(pdfKey)
    var windowReference: any = window.open()
    windowReference.location = res.url
    setLoading(false)
  }

  const onSync = async () => {
    setLoading(true)
    setAction('sync')
    syncNationalFlood({ propertyKey: selectedPropertyKey })
      .then(() => {})
      .finally(() => {
        setLoading(false)
        setAction('')
      })
  }

  return (
    <div className={`National-Flood-container ${!child && 'px-2 py-6'}`}>
      {!child && <Overview title={screenTitle} />}
      <div className="max-w-screen-2xl m-auto">
        <LayoutLoading show={loading} />
        <div className={`relative bg-white rounded text-center ${!child && 'shadow1 rounded mb-6 p-4'}`}>
          <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
            <span>{screenTitle}</span>
            {loading && <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin ml-2" />}
          </h2>
          {properties.length > 1 && (
            <div className="mb-4 flex justify-center">
              <ButtonGroup title={propertyTabs} value={selectedPropertyKey} onChange={onChangePropertyTab} />
            </div>
          )}
          <div className="overflow-auto mb-6 sm:rounded-lg">
            <p className="text-[19px] font-bold mb-3">Order Processing</p>
            <div className="overflow-auto shadow-md sm:rounded-lg">
              <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
                <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
                  <tr>
                    <th scope="col" className="px-3 py-3">
                      Order No
                    </th>
                    <th scope="col" className="px-3 py-3">
                      Vendor
                    </th>
                    <th scope="col" className="px-3 py-3 min-w-[200px]">
                      Certificate Number
                    </th>
                    <th scope="col" className="px-3 py-3">
                      Product Type
                    </th>
                    <th scope="col" className="px-3 py-3">
                      Condition
                    </th>
                    <th scope="col" className="px-3 py-3">
                      Description
                    </th>
                    <th scope="col" className="px-3 py-3">
                      Name
                    </th>
                    <th scope="col" className="px-3 py-3">
                      Order Date (by)
                    </th>
                    <th scope="col" className="px-3 py-3">
                      Request Type
                    </th>
                  </tr>
                </thead>
                <tbody className="text-[15px] text-gray-900">
                  {visibleOrders.map((order, index: number) => (
                    <tr key={`${order.order_no}-${index}`} className={`${index % 2 ? 'bg-gray-50' : ''}`}>
                      <td className="px-3 py-3">{order.order_no}</td>
                      <td className="px-3 py-3">{ServiceLinkOptions[order.vendor]}</td>
                      <td className="px-3 py-3">{order.certificate_number}</td>
                      <td className="px-3 py-3">{floodProductTypes[order.vendor]?.[order.product] || order.product}</td>
                      <td className="px-3 py-3 statusColor">
                        <span className="flex items-center">
                          <p className={`${order.condition} p-2 inline-block rounded`}>{order.condition}</p>
                          {order.pdf_flag === 0 ? (
                            ''
                          ) : (
                            <span
                              className="ml-1 text-shade-blue cursor-pointer color1 fz15 p-1 rounded hover-shadow1"
                              onClick={() => onDownloadDocument(order.pdfKey)}
                            >
                              <ArrowDownTrayIcon className="w-4 h-4" />
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-3">{order.description}</td>
                      <td className="px-3 py-3">{order._name}</td>
                      <td className="px-3 py-3">
                        <p>{order.req_time}</p>
                        <p>{order.by}</p>
                      </td>
                      <td className="px-3 py-3">{order.action}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Button onClick={onSubmitOrder} className="mb-[5px]">
            Submit Order
          </Button>
        </div>

        <div className="grid gap-x-3 grid-cols-1 md:grid-cols-2 w-full mt-4">
          <div className="relative bg-white shadow border rounded mb-2 p-4">
            <LayoutLoading show={borrowerLoading} />
            <p className="text-[18px] font-bold mb-3">
              Borrower & Property Info
              {borrowerLoading && <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin ml-2" />}
            </p>
            {Object.keys(borrowerPropertyInputGroups).map((title) => {
              if (title == 'Co-Borrower Info' && !inputs.coFirstName) return null

              const inputGroup = (borrowerPropertyInputGroups as any)[title]
              return (
                <div className="mb-5" key={title}>
                  <p className="border-b font-bold text-sm w-full mb-2 bg-gray-100 p-2 rounded">{title}</p>
                  <div className="grid gap-4 sm:grid-cols-2 grid-cols-1 mb-3 px-2">
                    {inputGroup.map((key: string) => {
                      const input = inputs[key]
                      return (
                        <div className={`input md:col-span-${input.span || 1}`} key={key}>
                          <RenderInput
                            input={input}
                            Key={key}
                            onChange={onChangeInput}
                            onBlur={(key: string) => onBlurInput(key, 0)}
                          />
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>

          <div className="relative shadow border rounded mb-2 p-4  bg-gray-50">
            <LayoutLoading show={floodLoading} />
            <p className="text-[18px] font-bold mb-3">
              Flood Results & HMDA Info
              {floodLoading && <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin ml-2" />}
            </p>
            {Object.keys(floodResultInfoInputGroups).map((title) => {
              const inputGroup = (floodResultInfoInputGroups as any)[title]
              return (
                <div className="mb-5" key={title}>
                  <p className="border-b font-bold text-sm w-full mb-3 bg-blue-100 p-2 rounded">{title}</p>
                  <div className="grid gap-4 sm:grid-cols-2 grid-cols-1 mb-3 px-2">
                    {inputGroup.map((key: string) => {
                      let input = inputs[key] as any
                      return (
                        <div className={`input md:col-span-${input.span || 1}`} key={key}>
                          <RenderInput
                            input={input}
                            Key={key}
                            onChange={onChangeInput}
                            onBlur={(key: string) => onBlurInput(key, 1)}
                          />
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
            <div className="text-center">
              <Button onClick={onSync} className="px-[40px]" loading={action === 'sync'}>
                Sync
              </Button>
            </div>
          </div>
        </div>
      </div>

      {isModalOpened && (
        <ServiceLinkOrderModal
          certificateNumbersByVendor={certificateNumbersByVendor}
          onClose={onCloseModal}
          onSubmit={onSubmitModal}
        />
      )}
    </div>
  )
}
